import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useP131KQuery } from './hooks/useP131KQuery';
import type { ReportRow } from '../../services/p131kApi';
import './P131KForm.css';

const P131KForm: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = useP131KQuery();

  const [empCodeFrom, setEmpCodeFrom] = useState('');
  const [empCodeTo, setEmpCodeTo] = useState('');

  const handleQuery = () => {
    runQuery({
      emp_code_from: empCodeFrom.trim() || undefined,
      emp_code_to: empCodeTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setEmpCodeFrom('');
    setEmpCodeTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>員工簡碼清冊</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 8px}th{background:#f0f0f0;text-align:center}@media print{body{margin:10mm}}</style></head><body>
<h2>員工簡碼清冊</h2>
<table><thead><tr><th>員工代號</th><th>員工人事代號</th><th>員工姓名</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.F11K_01}</td><td>${r.F11K_11}</td><td>${r.emp_name || ''}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '員工代號', dataIndex: 'F11K_01', key: 'F11K_01', width: 120 },
    { title: '員工人事代號', dataIndex: 'F11K_11', key: 'F11K_11', width: 140 },
    { title: '員工姓名', dataIndex: 'emp_name', key: 'emp_name', width: 150 },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.P131K} action="query">
      <div className="p131k-container">
        <div className="p131k-header">
          <h2>員工簡碼清冊</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              {filterLabel('員工代號起')}
              <Input value={empCodeFrom} onChange={e => setEmpCodeFrom(e.target.value)} placeholder="起" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('員工代號迄')}
              <Input value={empCodeTo} onChange={e => setEmpCodeTo(e.target.value)} placeholder="迄" style={{ width: 140 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<ReportRow>
            columns={columns}
            dataSource={data}
            rowKey={(r, i) => `${r.F11K_01}-${i}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 500 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default P131KForm;
